import { useState, useCallback } from 'react';
import { Drawer } from '@navi/web-ui/lib/primitives';
import { DrawerFooter } from '@cp/src/components/Drawers/DrawerFooter';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '@cp/src/store';
import UploadFileDrawer from './UploadFileDrawer';
import { CircularLoaderIcon } from '@navi/web-ui/lib/icons';
import { getAllExperiments, updateFile } from '../../actions/FieldAllocationAction';
import { toast } from '@navi/web-ui/lib/primitives/Toast';

interface IUploadLanDrawerProps {
  isDrawerOpen: boolean;
  onClose: () => void;
  setIsDrawerOpen: (value: boolean) => void;
}

const UploadLanDrawer = (props: IUploadLanDrawerProps) => {
  const { isDrawerOpen, onClose } = props;
  const dispatch = useDispatch();
  const experimentData = useSelector((state: RootState) => state?.fieldAllocation?.experimentData);
  const isLoading = useSelector((state: RootState) => state?.fieldAllocation?.isLoading);
  const [isOutsideClick, setIsOutsideClicked] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const { originalFileUri, name: experimentName } = experimentData || {};
  const [userRemovedFile, setUserRemovedFile] = useState(false);
  const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState(!originalFileUri);
  const isDirtyField = !!uploadedFile || userRemovedFile;

  const onFinally = () => {
    dispatch(getAllExperiments());
    toast('File uploaded successfully!', { type: 'success', delay: 500 });
    onClose();
  };

  const handleSubmitClick = useCallback(() => {
    if (uploadedFile && experimentName) {
      dispatch(updateFile(uploadedFile, experimentName, onFinally));
      setUserRemovedFile(false);
      setUploadedFile(null);
      setIsSubmitButtonDisabled(false);
    }
  }, [dispatch, uploadedFile, experimentName]);

  const handleExitYesClick = () => {
    setIsOutsideClicked(false);
    setUserRemovedFile(false);
    setUploadedFile(null);
    setIsSubmitButtonDisabled(false);
    onClose();
  };

  const handleExitNoClick = () => {
    setIsOutsideClicked(false);
  };

  const toggleOutsideClick = () => {
    if (isDirtyField) {
      setIsOutsideClicked(true);
    } else {
      setIsOutsideClicked(false);
      onClose();
    }
  };
  const onFileSelect = (file: File | null) => {
    setUploadedFile(file);
    if (userRemovedFile && !file) {
      setIsSubmitButtonDisabled(true);
    } else {
      setIsSubmitButtonDisabled(false);
    }
  };
  const handleFileRemove = () => {
    setUserRemovedFile(true);
    setUploadedFile(null);
    setIsSubmitButtonDisabled(true);
  };

  return (
    <Drawer
      open={isDrawerOpen}
      headerText={originalFileUri ? `Edit LAN's` : `Upload LAN's`}
      showHeaderDivider={true}
      usePadding={false}
      contentClasses="overflow-auto"
      onClose={isDirtyField ? toggleOutsideClick : onClose}
      onOutSideClick={toggleOutsideClick}
      className="!w-[400px]"
      disableOutsideClick={isDirtyField}
      footerContainerClasses={isOutsideClick ? '!bg-[var(--navi-color-red-bg)]' : ''}
      footer={
        <DrawerFooter
          handleSubmitClick={handleSubmitClick}
          isSubmitButtonDisabled={isSubmitButtonDisabled}
          handleExitYesClick={handleExitYesClick}
          handleExitNoClick={handleExitNoClick}
          onClose={onClose}
          toggleOutsideClick={toggleOutsideClick}
          isDirtyField={isDirtyField}
          isOutsideClick={isOutsideClick}
          isEditPage={true}
          buttonTitle={'Upload'}
          primaryButtonTitle={`Upload LANs`}
        />
      }
    >
      {isLoading ? (
        <div className="flex items-center justify-center h-full">
          <CircularLoaderIcon size="lg" />
        </div>
      ) : (
        <UploadFileDrawer
          handleFileRemove={handleFileRemove}
          onFileSelect={onFileSelect}
          userRemovedFile={userRemovedFile}
        />
      )}
    </Drawer>
  );
};

export default UploadLanDrawer;
